import type { ReactNode } from 'react'

const seeded = (seed: number) => {
    let state = seed

    return () => {
        state = (state * 1664525 + 1013904223) >>> 0

        return state / 4294967296
    }
}

const axisText = { fill: 'var(--c-star-3)', fontSize: 9, fontFamily: 'var(--font-app-mono, monospace)', letterSpacing: 0.6 } as const

/** Algol through one primary eclipse: brightness against hours, with the fitted curve. */
export const EclipseFigure = (): ReactNode => {
    const random = seeded(77)
    const width = 420
    const height = 220
    const left = 44
    const right = 16
    const top = 18
    const bottom = 34
    const plotWidth = width - left - right
    const plotHeight = height - top - bottom
    const magnitude = (t: number) => 2.12 + 1.27 * Math.exp(-Math.pow((t - 5.0) / 1.15, 2))
    const x = (t: number) => left + (t / 10) * plotWidth
    const y = (m: number) => top + ((m - 1.95) / (3.55 - 1.95)) * plotHeight
    const curve: string[] = []

    for (let t = 0; t <= 10; t += 0.05) curve.push(`${x(t).toFixed(1)},${y(magnitude(t)).toFixed(1)}`)

    const points = Array.from({ length: 58 }, (_, index) => {
        const t = 0.2 + index * 0.168

        return { t, m: magnitude(t) + (random() - 0.5) * 0.06 }
    })

    return (
        <figure>
            <svg viewBox={`0 0 ${width} ${height}`} className='block w-full' role='img' aria-label='Light curve of Algol: its brightness drops by more than a magnitude for about ten hours during the eclipse.'>
                <line x1={left} y1={top} x2={left} y2={top + plotHeight} stroke='var(--c-hair-2)' strokeWidth={1} />
                <line x1={left} y1={top + plotHeight} x2={left + plotWidth} y2={top + plotHeight} stroke='var(--c-hair-2)' strokeWidth={1} />
                {[2, 2.5, 3, 3.5].map(m => (
                    <g key={m}>
                        <line x1={left - 4} y1={y(m)} x2={left} y2={y(m)} stroke='var(--c-hair-2)' />
                        <text x={left - 8} y={y(m) + 3} textAnchor='end' {...axisText}>
                            {m.toFixed(1)}
                        </text>
                    </g>
                ))}
                {[0, 2, 4, 6, 8, 10].map(t => (
                    <g key={t}>
                        <line x1={x(t)} y1={top + plotHeight} x2={x(t)} y2={top + plotHeight + 4} stroke='var(--c-hair-2)' />
                        <text x={x(t)} y={top + plotHeight + 16} textAnchor='middle' {...axisText}>
                            {`${t}h`}
                        </text>
                    </g>
                ))}
                <polyline points={curve.join(' ')} fill='none' stroke='var(--c-brass)' strokeWidth={1.2} />
                {points.map(point => (
                    <circle key={point.t} cx={x(point.t)} cy={y(point.m)} r={1.6} fill='var(--c-star-2)' />
                ))}
                <line x1={x(5)} y1={top} x2={x(5)} y2={top + plotHeight} stroke='var(--c-brass)' strokeDasharray='2 4' strokeWidth={0.8} />
                <text x={x(5) + 6} y={top + 10} {...axisText} fill='var(--c-brass)'>
                    MINIMUM 23:41 BST
                </text>
                <text x={left} y={top - 6} {...axisText}>
                    V MAGNITUDE
                </text>
            </svg>
            <figcaption className='label-mono mt-3 text-[0.6rem] text-star-3'>
                Fig. 1 · Algol, 12 October 2026. 380 frames, 40 cm reflector, V filter.
            </figcaption>
        </figure>
    )
}

/** Seven years of Algol minima, each compared with the prediction: the drift is the story. */
export const DriftFigure = (): ReactNode => {
    const random = seeded(19)
    const width = 420
    const height = 220
    const left = 44
    const right = 16
    const top = 18
    const bottom = 34
    const plotWidth = width - left - right
    const plotHeight = height - top - bottom
    const x = (year: number) => left + ((year - 2019) / 8) * plotWidth
    const y = (minutes: number) => top + ((5 - minutes) / 7) * plotHeight
    const points = Array.from({ length: 34 }, (_, index) => {
        const year = 2019.2 + index * 0.22
        const trend = -0.6 + (year - 2019) * 0.62

        return { year, minutes: trend + (random() - 0.5) * 1.1, error: 0.3 + random() * 0.4 }
    })

    return (
        <figure>
            <svg viewBox={`0 0 ${width} ${height}`} className='block w-full' role='img' aria-label='Timings of Algol eclipses since 2019 arriving progressively later than predicted, by about four minutes by 2026.'>
                <line x1={left} y1={top} x2={left} y2={top + plotHeight} stroke='var(--c-hair-2)' />
                <line x1={left} y1={top + plotHeight} x2={left + plotWidth} y2={top + plotHeight} stroke='var(--c-hair-2)' />
                <line x1={left} y1={y(0)} x2={left + plotWidth} y2={y(0)} stroke='var(--c-hair)' strokeDasharray='3 4' />
                {[-2, 0, 2, 4].map(minutes => (
                    <text key={minutes} x={left - 8} y={y(minutes) + 3} textAnchor='end' {...axisText}>
                        {minutes > 0 ? `+${minutes}` : minutes}
                    </text>
                ))}
                {[2019, 2021, 2023, 2025, 2027].map(year => (
                    <text key={year} x={x(year)} y={top + plotHeight + 16} textAnchor='middle' {...axisText}>
                        {year}
                    </text>
                ))}
                <line x1={x(2019)} y1={y(-0.6)} x2={x(2027)} y2={y(-0.6 + 8 * 0.62)} stroke='var(--c-brass)' strokeWidth={1.2} />
                {points.map(point => (
                    <g key={point.year}>
                        <line x1={x(point.year)} y1={y(point.minutes - point.error)} x2={x(point.year)} y2={y(point.minutes + point.error)} stroke='var(--c-star-3)' strokeWidth={0.8} />
                        <circle cx={x(point.year)} cy={y(point.minutes)} r={1.8} fill='var(--c-star)' />
                    </g>
                ))}
                <text x={left} y={top - 6} {...axisText}>
                    OBSERVED MINUS PREDICTED, MINUTES
                </text>
            </svg>
            <figcaption className='label-mono mt-3 text-[0.6rem] text-star-3'>
                Fig. 2 · Thirty-four timed minima of Algol, 2019 to 2026.
            </figcaption>
        </figure>
    )
}
